import React, { useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { apiService } from '../../services/api'
import {
  Clock,
  User,
  Edit,
  Filter,
  Search,
  ChevronLeft,
  ChevronRight,
  Code,
  Calendar,
  Activity,
  FileText,
  Plus,
  Trash2,
  Palette,
  Rocket,
  Save,
  BarChart3,
  RefreshCw,
  Download,
  Users,
  Globe,
  TrendingUp,
  X,
  CheckCircle,
  AlertCircle,
  Info,
  Zap,
  Monitor,
  Hash,
  Target,
  Settings,
  ChevronDown,
  MoreHorizontal
} from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import LoadingSpinner from '../common/LoadingSpinner'
import { Button } from '../ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { cn } from '../../utils/cn'
import ModernCalendar from '../ui/ModernCalendar'

// ── Helpers ───────────────────────────────────────────────────────────────────

// Local label map — fallback when the backend metadata doesn't include a type
const EDIT_TYPE_LABELS = {
  text_change:      'Text Change',
  new_page:         'New Page',
  visual_edit:      'Visual Edit',
  component_add:    'Component Added',
  component_delete: 'Component Deleted',
  style_change:     'Style Change',
  publish:          'Published',
  save_draft:       'Draft Saved',
  media_upload:     'Media Upload',
  settings_change:  'Settings Change',
  domain_add:       'Domain Added',
  domain_verify:    'Domain Verified',
  domain_delete:    'Domain Removed',
  domain_update:    'Domain Updated',
}

const getEditTypeLabel = (editType, metadata) => {
  if (!editType) return 'Other'
  return (
    metadata?.editTypes?.find(t => t.value === editType)?.label ||
    EDIT_TYPE_LABELS[editType] ||
    editType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
  )
}

const getEditTypeIcon = (editType) => {
  if (!editType) return Activity
  const icons = {
    text_change:      FileText,
    new_page:         Plus,
    visual_edit:      Edit,
    component_add:    Plus,
    component_delete: Trash2,
    style_change:     Palette,
    publish:          Rocket,
    save_draft:       Save,
    media_upload:     FileText,
    settings_change:  Settings,
    domain_add:       Globe,
    domain_verify:    Globe,
    domain_delete:    Globe,
    domain_update:    Globe,
  }
  return icons[editType] || Edit
}

const getEditTypeColor = (editType) => {
  const colors = {
    text_change:      'text-primary-600 bg-primary-100 dark:text-primary-400 dark:bg-primary-900/20',
    new_page:         'text-emerald-600 bg-emerald-100 dark:text-emerald-400 dark:bg-emerald-900/20',
    visual_edit:      'text-accent-600 bg-accent-100 dark:text-accent-400 dark:bg-accent-900/20',
    component_add:    'text-emerald-600 bg-emerald-100 dark:text-emerald-400 dark:bg-emerald-900/20',
    component_delete: 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900/20',
    style_change:     'text-pink-600 bg-pink-100 dark:text-pink-400 dark:bg-pink-900/20',
    publish:          'text-orange-600 bg-orange-100 dark:text-orange-400 dark:bg-orange-900/20',
    save_draft:       'text-neutral-600 bg-neutral-100 dark:text-neutral-400 dark:bg-neutral-800/60',
    media_upload:     'text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/20',
    settings_change:  'text-purple-600 bg-purple-100 dark:text-purple-400 dark:bg-purple-900/20',
    domain_add:       'text-cyan-600 bg-cyan-100 dark:text-cyan-400 dark:bg-cyan-900/20',
    domain_verify:    'text-teal-600 bg-teal-100 dark:text-teal-400 dark:bg-teal-900/20',
    domain_delete:    'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900/20',
    domain_update:    'text-cyan-600 bg-cyan-100 dark:text-cyan-400 dark:bg-cyan-900/20',
  }
  return colors[editType] || 'text-neutral-600 bg-neutral-100 dark:text-neutral-400 dark:bg-neutral-800/60'
}

const getEditTypeBadge = (editType) => {
  const badges = {
    text_change:      'primary',
    new_page:         'success',
    visual_edit:      'accent',
    component_add:    'emerald',
    component_delete: 'error',
    style_change:     'pink',
    publish:          'orange',
    save_draft:       'neutral',
    media_upload:     'blue',
    settings_change:  'purple',
    domain_add:       'primary',
    domain_verify:    'success',
    domain_delete:    'error',
    domain_update:    'primary',
  }
  return badges[editType] || 'neutral'
}

const formatRelativeTime = (timestamp) => {
  const diff = Date.now() - new Date(timestamp).getTime()
  const mins = Math.floor(diff / 60000)
  const hrs  = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)
  if (mins < 1)  return 'Just now'
  if (mins < 60) return `${mins}m ago`
  if (hrs  < 24) return `${hrs}h ago`
  if (days < 7)  return `${days}d ago`
  return new Date(timestamp).toLocaleString()
}

const DiffBadges = ({ diffData }) => {
  if (!diffData?.stats) return null
  const { charactersAdded, charactersRemoved } = diffData.stats
  if (!charactersAdded && !charactersRemoved) return null
  return (
    <div className="flex items-center gap-1.5">
      {charactersAdded > 0 && (
        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium text-emerald-700 bg-emerald-100 dark:text-emerald-400 dark:bg-emerald-900/20">
          +{charactersAdded}
        </span>
      )}
      {charactersRemoved > 0 && (
        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium text-red-700 bg-red-100 dark:text-red-400 dark:bg-red-900/20">
          -{charactersRemoved}
        </span>
      )}
    </div>
  )
}

// ── Pagination sub-component ──────────────────────────────────────────────────

const Pagination = ({ pagination, onPageChange, limit, onLimitChange }) => {
  if (!pagination) return null
  const { page, pages, total, limit: pgLimit } = pagination
  const from = ((page - 1) * pgLimit) + 1
  const to   = Math.min(page * pgLimit, total)
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
      <div className="flex items-center gap-3">
        <span className="text-sm text-neutral-500 dark:text-neutral-400">
          <span className="hidden sm:inline">Showing </span>
          {from}–{to} of {total}
        </span>
        {onLimitChange && (
          <select
            value={limit}
            onChange={(e) => onLimitChange(parseInt(e.target.value))}
            className="h-8 px-2 text-sm border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-dark-200 text-neutral-700 dark:text-neutral-300 focus:outline-none focus:ring-2 focus:ring-accent-500"
          >
            <option value={25}>25 / page</option>
            <option value={50}>50 / page</option>
            <option value={100}>100 / page</option>
          </select>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page - 1)}
          disabled={page === 1}
        >
          <ChevronLeft className="h-4 w-4" />
          <span className="hidden sm:inline ml-1">Prev</span>
        </Button>
        <span className="text-sm text-neutral-500 dark:text-neutral-400 px-1">
          {page} / {pages}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page + 1)}
          disabled={page === pages}
        >
          <span className="hidden sm:inline mr-1">Next</span>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

const EditLogsDashboard = () => {
  const queryClient = useQueryClient()
  const [filters, setFilters] = useState({
    siteId: '',
    userId: '',
    editType: '',
    action: '',
    startDate: '',
    endDate: '',
    page: 1,
    limit: 50,
    search: ''
  })
  const [showFilters, setShowFilters] = useState(false)
  const [selectedLog, setSelectedLog] = useState(null)
  const [viewMode, setViewMode] = useState('timeline')
  const [isAutoRefresh, setIsAutoRefresh] = useState(false)

  // ── Queries ────────────────────────────────────────────────────────────────

  const { data: logsData, isLoading, isFetching, error, refetch } = useQuery(
    ['edit-logs', filters],
    () => {
      const params = new URLSearchParams()
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value)
      })
      return apiService.get(`/edit-logs?${params.toString()}`).then(res => res.data)
    },
    {
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000,
      refetchInterval: isAutoRefresh && !selectedLog ? 30000 : false,
      refetchIntervalInBackground: false,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false
    }
  )

  const { data: statsData, isLoading: statsLoading } = useQuery(
    ['edit-stats', filters.siteId, filters.userId, filters.startDate, filters.endDate],
    () => {
      const params = new URLSearchParams()
      if (filters.siteId)    params.append('siteId',    filters.siteId)
      if (filters.userId)    params.append('userId',    filters.userId)
      if (filters.startDate) params.append('startDate', filters.startDate)
      if (filters.endDate)   params.append('endDate',   filters.endDate)
      return apiService.get(`/edit-logs/stats?${params.toString()}`).then(res => res.data)
    },
    {
      enabled: viewMode === 'stats',
      staleTime: 5 * 60 * 1000,
      refetchInterval: false,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false
    }
  )

  const { data: metadata } = useQuery(
    'edit-metadata',
    () => apiService.get('/edit-logs/metadata').then(res => res.data),
    {
      staleTime: 300000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false
    }
  )

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleCloseModal = () => setSelectedLog(null)

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }))
  }

  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }))
  }

  const handleLimitChange = (newLimit) => {
    setFilters(prev => ({ ...prev, limit: newLimit, page: 1 }))
  }

  const clearFilters = () => {
    setFilters({ siteId: '', userId: '', editType: '', action: '', startDate: '', endDate: '', page: 1, limit: 50, search: '' })
  }

  const exportLogs = () => {
    const params = new URLSearchParams()
    Object.entries(filters).forEach(([key, value]) => {
      if (value && key !== 'page' && key !== 'limit') params.append(key, value)
    })
    window.open(`/edit-logs/export?${params.toString()}`, '_blank')
  }

  const activeFilterCount = Object.entries(filters).filter(([key, value]) =>
    value && !['page', 'limit'].includes(key)
  ).length

  // ── Sub-views ──────────────────────────────────────────────────────────────

  const TimelineView = ({ logs }) => (
    <Card className="bg-white dark:bg-dark-200 border border-neutral-200 dark:border-neutral-700 overflow-hidden">
      {!logs?.length ? (
        <CardContent className="py-16 text-center text-neutral-400 dark:text-neutral-500 text-sm">
          No edit logs found.
        </CardContent>
      ) : (
        <div>
          {logs.map((log, idx) => {
            const Icon = getEditTypeIcon(log.edit_type)
            const isLast = idx === logs.length - 1
            return (
              <div
                key={log.id}
                onClick={() => setSelectedLog(log)}
                className={cn(
                  'flex items-start gap-4 px-5 py-4 cursor-pointer transition-colors hover:bg-accent-50/50 dark:hover:bg-accent-900/10 group',
                  !isLast && 'border-b border-neutral-100 dark:border-neutral-700/40'
                )}
              >
                <div className={cn(
                  'flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center mt-0.5',
                  getEditTypeColor(log.edit_type)
                )}>
                  <Icon className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2 flex-wrap min-w-0">
                      <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 truncate">
                        {log.user_name}
                      </span>
                      <span className={cn(
                        'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium shrink-0',
                        `badge-${getEditTypeBadge(log.edit_type)}`
                      )}>
                        {getEditTypeLabel(log.edit_type, metadata)}
                      </span>
                    </div>
                    <span className="text-xs text-neutral-400 dark:text-neutral-500 shrink-0">
                      {formatRelativeTime(log.created_at)}
                    </span>
                  </div>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5 leading-relaxed">
                    {log.description}
                  </p>
                  <div className="flex items-center justify-between mt-1.5">
                    <div className="flex items-center gap-1.5 text-xs text-neutral-400 dark:text-neutral-500">
                      <Globe className="h-3 w-3" />
                      <span>{log.site_name}</span>
                      <span>·</span>
                      <span>{log.company_name}</span>
                    </div>
                    <DiffBadges diffData={log.diff_data} />
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </Card>
  )

  const ListView = ({ logs }) => (
    <Card className="bg-white dark:bg-dark-200 border border-neutral-200 dark:border-neutral-700 overflow-hidden">
      {/* Desktop table */}
      <div className="hidden sm:block overflow-x-auto">
        <table className="w-full divide-y divide-neutral-100 dark:divide-neutral-700/40">
          <thead className="bg-neutral-50 dark:bg-dark-300">
            <tr>
              {['Activity', 'User', 'Site', 'Changes', 'Time'].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-dark-200 divide-y divide-neutral-100 dark:divide-neutral-700/40">
            {logs?.map((log) => {
              const Icon = getEditTypeIcon(log.edit_type)
              return (
                <tr
                  key={log.id}
                  onClick={() => setSelectedLog(log)}
                  className="hover:bg-accent-50/50 dark:hover:bg-accent-900/10 transition-colors cursor-pointer"
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className={cn('h-8 w-8 rounded-xl flex items-center justify-center flex-shrink-0', getEditTypeColor(log.edit_type))}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate">
                          {getEditTypeLabel(log.edit_type, metadata)}
                        </div>
                        <div className="text-xs text-neutral-400 dark:text-neutral-500 truncate max-w-[180px]">
                          {log.description}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="h-6 w-6 rounded-full bg-gradient-to-br from-accent-500 to-accent-600 flex items-center justify-center shrink-0">
                        <span className="text-white text-xs font-medium">
                          {log.user_name?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                      <span className="text-sm text-neutral-700 dark:text-neutral-300 truncate">{log.user_name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate">{log.site_name}</div>
                    <div className="text-xs text-neutral-400 dark:text-neutral-500 truncate">{log.company_name}</div>
                  </td>
                  <td className="px-4 py-3">
                    <DiffBadges diffData={log.diff_data} />
                  </td>
                  <td className="px-4 py-3 text-sm text-neutral-500 dark:text-neutral-400 whitespace-nowrap">
                    {formatRelativeTime(log.created_at)}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile card rows */}
      <div className="sm:hidden divide-y divide-neutral-100 dark:divide-neutral-700/40">
        {logs?.map((log) => {
          const Icon = getEditTypeIcon(log.edit_type)
          return (
            <div
              key={log.id}
              onClick={() => setSelectedLog(log)}
              className="flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-accent-50/50 dark:hover:bg-accent-900/10 transition-colors"
            >
              <div className={cn('h-8 w-8 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5', getEditTypeColor(log.edit_type))}>
                <Icon className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate">
                    {getEditTypeLabel(log.edit_type, metadata)}
                  </span>
                  <span className="text-xs text-neutral-400 dark:text-neutral-500 shrink-0">
                    {formatRelativeTime(log.created_at)}
                  </span>
                </div>
                <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 truncate">{log.description}</p>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-xs text-neutral-400 dark:text-neutral-500">{log.user_name} · {log.site_name}</span>
                  <DiffBadges diffData={log.diff_data} />
                </div>
              </div>
            </div>
          )
        })}
        {!logs?.length && (
          <div className="py-12 text-center text-sm text-neutral-400 dark:text-neutral-500">No edit logs found.</div>
        )}
      </div>
    </Card>
  )

  const StatCard = ({ title, value, icon: Icon, index = 0 }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.08 }}
    >
      <Card className="bg-white dark:bg-dark-200 border border-neutral-200 dark:border-neutral-700">
        <CardContent className="p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-500 dark:text-neutral-400">{title}</p>
              <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-50 mt-1">
                {value?.toLocaleString() ?? 0}
              </p>
            </div>
            <div className="h-11 w-11 bg-accent-600 rounded-xl flex items-center justify-center">
              <Icon className="h-5 w-5 text-white" />
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  )

  const StatsView = ({ stats }) => (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Total Edits"    value={stats?.reduce((s, x) => s + x.count, 0) || 0}          icon={Edit}    index={0} />
        <StatCard title="Active Users"   value={stats?.reduce((s, x) => s + x.unique_users, 0) || 0}   icon={Users}   index={1} />
        <StatCard title="Sites Updated"  value={stats?.length || 0}                                     icon={Globe}   index={2} />
        <StatCard title="Publishes"      value={stats?.find(x => x.edit_type === 'publish')?.count || 0} icon={Rocket}  index={3} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {stats?.map((stat, index) => {
          const Icon = getEditTypeIcon(stat.edit_type)
          return (
            <motion.div
              key={stat.edit_type}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.06 }}
            >
              <Card className="bg-white dark:bg-dark-200 border border-neutral-200 dark:border-neutral-700">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className={cn('h-8 w-8 rounded-xl flex items-center justify-center', getEditTypeColor(stat.edit_type))}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                      {getEditTypeLabel(stat.edit_type, metadata)}
                    </h3>
                  </div>
                  <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-50">{stat.count}</p>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                    {stat.unique_users} users
                  </p>
                  <div className="mt-3 pt-3 border-t border-neutral-100 dark:border-neutral-700/40 flex justify-between text-xs text-neutral-400 dark:text-neutral-500">
                    <span>First: {formatRelativeTime(stat.first_edit)}</span>
                    <span>Last: {formatRelativeTime(stat.last_edit)}</span>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )
        })}
      </div>
    </div>
  )

  // ── Error state ────────────────────────────────────────────────────────────

  if (error) {
    return (
      <div className="pt-4 text-center py-12">
        <div className="mx-auto w-14 h-14 bg-red-100 dark:bg-red-900/20 rounded-2xl flex items-center justify-center mb-4">
          <AlertCircle className="h-7 w-7 text-red-500" />
        </div>
        <h3 className="text-base font-semibold text-neutral-900 dark:text-neutral-100 mb-1">
          Error loading edit logs
        </h3>
        <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-4">{error.message}</p>
        <Button onClick={refetch} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </Button>
      </div>
    )
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6 pt-4">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-neutral-900 dark:text-neutral-50">
            Edit Logs
          </h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
            Track all website edits and changes across your platform
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Auto-refresh */}
          <Button
            variant={isAutoRefresh ? 'default' : 'outline'}
            size="sm"
            onClick={() => setIsAutoRefresh(!isAutoRefresh)}
          >
            <RefreshCw className={cn('h-4 w-4 sm:mr-2', isFetching && 'animate-spin')} />
            <span className="hidden sm:inline">Auto-refresh</span>
          </Button>

          {/* Export */}
          <Button variant="outline" size="sm" onClick={exportLogs}>
            <Download className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Export</span>
          </Button>

          {/* View switcher */}
          <div className="flex bg-neutral-100 dark:bg-dark-100 rounded-lg p-1">
            {[
              { key: 'timeline', icon: Activity,   label: 'Timeline' },
              { key: 'list',     icon: BarChart3,  label: 'List'     },
              { key: 'stats',    icon: TrendingUp, label: 'Stats'    },
            ].map(({ key, icon: Icon, label }) => (
              <button
                key={key}
                onClick={() => setViewMode(key)}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
                  viewMode === key
                    ? 'bg-white dark:bg-dark-200 text-accent-600 shadow-sm'
                    : 'text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200'
                )}
              >
                <Icon className="h-4 w-4" />
                <span className="hidden sm:inline">{label}</span>
              </button>
            ))}
          </div>

          {/* Filters toggle */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className={activeFilterCount > 0 ? 'border-accent-400 dark:border-accent-600' : ''}
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
            {activeFilterCount > 0 && (
              <span className="ml-1.5 text-xs bg-accent-600 text-white rounded-full px-1.5 py-0.5 leading-none">
                {activeFilterCount}
              </span>
            )}
          </Button>
        </div>
      </div>

      {/* Filter panel */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
          >
            <Card className="bg-white dark:bg-dark-200 border border-neutral-200 dark:border-neutral-700">
              <CardContent className="p-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Search */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-neutral-600 dark:text-neutral-400">Search</label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
                      <input
                        type="text"
                        placeholder="Search logs…"
                        value={filters.search}
                        onChange={(e) => handleFilterChange('search', e.target.value)}
                        className="w-full pl-9 pr-3 py-2 text-sm border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-dark-300 text-neutral-900 dark:text-neutral-100 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-accent-500"
                      />
                    </div>
                  </div>

                  {/* Edit Type */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-neutral-600 dark:text-neutral-400">Edit Type</label>
                    <div className="relative">
                      <select
                        value={filters.editType}
                        onChange={(e) => handleFilterChange('editType', e.target.value)}
                        className="w-full px-3 py-2 pr-8 text-sm border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-dark-300 text-neutral-900 dark:text-neutral-100 appearance-none focus:outline-none focus:ring-2 focus:ring-accent-500"
                      >
                        <option value="">All Types</option>
                        {metadata?.editTypes?.map(type => (
                          <option key={type.value} value={type.value}>{type.label}</option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400 pointer-events-none" />
                    </div>
                  </div>

                  {/* Start Date */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-neutral-600 dark:text-neutral-400">Start Date</label>
                    <ModernCalendar
                      value={filters.startDate ? `${filters.startDate}T00:00:00` : ''}
                      onChange={(iso) => handleFilterChange('startDate', iso ? iso.slice(0, 10) : '')}
                      placeholder="Select start date"
                      includeTime={false}
                    />
                  </div>

                  {/* End Date */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-neutral-600 dark:text-neutral-400">End Date</label>
                    <ModernCalendar
                      value={filters.endDate ? `${filters.endDate}T00:00:00` : ''}
                      onChange={(iso) => handleFilterChange('endDate', iso ? iso.slice(0, 10) : '')}
                      placeholder="Select end date"
                      includeTime={false}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-end gap-2 mt-4 pt-4 border-t border-neutral-100 dark:border-neutral-700/40">
                  {activeFilterCount > 0 && (
                    <Button variant="ghost" size="sm" onClick={clearFilters}>
                      <X className="h-4 w-4 mr-1.5" />
                      Clear filters
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={() => setShowFilters(false)}>
                    Close
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Content */}
      {isLoading || (viewMode === 'stats' && statsLoading) ? (
        <div className="flex items-center justify-center py-16 gap-3 text-neutral-500 dark:text-neutral-400">
          <div className="animate-spin rounded-full h-5 w-5 border-2 border-accent-600 border-t-transparent" />
          <span className="text-sm">Loading edit logs…</span>
        </div>
      ) : (
        <div className="space-y-4">
          {viewMode === 'timeline' && (
            <>
              <TimelineView logs={logsData?.logs} />
              <Pagination
                pagination={logsData?.pagination}
                onPageChange={handlePageChange}
                limit={filters.limit}
                onLimitChange={handleLimitChange}
              />
            </>
          )}

          {viewMode === 'list' && (
            <>
              <ListView logs={logsData?.logs} />
              <Pagination
                pagination={logsData?.pagination}
                onPageChange={handlePageChange}
                limit={filters.limit}
                onLimitChange={handleLimitChange}
              />
            </>
          )}

          {viewMode === 'stats' && (
            <StatsView stats={statsData} />
          )}
        </div>
      )}

      {/* Log Detail Modal */}
      <AnimatePresence>
        {selectedLog && (
          <LogDetailModal
            log={selectedLog}
            onClose={handleCloseModal}
            metadata={metadata}
          />
        )}
      </AnimatePresence>
    </div>
  )
}

// ── Log Detail Modal ──────────────────────────────────────────────────────────

const LogDetailModal = ({ log, onClose, metadata }) => {
  const Icon = getEditTypeIcon(log.edit_type)

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[9999] overflow-y-auto bg-black/50 backdrop-blur-sm"
      style={{ margin: 0 }}
      onClick={onClose}
    >
      <div className="flex items-center justify-center min-h-screen p-4">
        <motion.div
          initial={{ scale: 0.96, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.96, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="relative w-full max-w-3xl"
          onClick={(e) => e.stopPropagation()}
        >
          <Card className="bg-white dark:bg-dark-200 border border-neutral-200 dark:border-neutral-700 shadow-2xl">
            {/* Modal header */}
            <CardHeader className="pb-4 pr-12">
              <div className="flex items-center gap-3">
                <div className={cn('h-11 w-11 rounded-xl flex items-center justify-center flex-shrink-0', getEditTypeColor(log.edit_type))}>
                  <Icon className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle className="text-lg">Edit Log Details</CardTitle>
                  <CardDescription className="text-sm mt-0.5">{log.description}</CardDescription>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="absolute top-3 right-3 h-9 w-9 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>

            <CardContent className="space-y-5 px-4 sm:px-6">
              {/* Basic + Technical info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-300">Basic Information</h3>
                  {[
                    ['Edit Type', getEditTypeLabel(log.edit_type, metadata)],
                    ['User',      log.user_name],
                    ['Site',      log.site_name],
                    ['Timestamp', new Date(log.created_at).toLocaleString()],
                  ].map(([label, val]) => (
                    <div key={label} className="flex items-center justify-between gap-2 px-3 py-2 bg-neutral-50 dark:bg-dark-300 rounded-lg">
                      <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400">{label}</span>
                      <span className="text-xs font-semibold text-neutral-900 dark:text-neutral-100 text-right">{val}</span>
                    </div>
                  ))}
                </div>

                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-300">Technical Details</h3>
                  {log.ip_address && (
                    <div className="flex items-center justify-between gap-2 px-3 py-2 bg-neutral-50 dark:bg-dark-300 rounded-lg">
                      <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400">IP Address</span>
                      <span className="text-xs font-mono text-neutral-900 dark:text-neutral-100">{log.ip_address}</span>
                    </div>
                  )}
                  {log.session_id && (
                    <div className="flex items-center justify-between gap-2 px-3 py-2 bg-neutral-50 dark:bg-dark-300 rounded-lg">
                      <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400 shrink-0">Session ID</span>
                      <span className="text-xs font-mono text-neutral-900 dark:text-neutral-100 truncate min-w-0">{log.session_id}</span>
                    </div>
                  )}
                  {log.before_hash && (
                    <div className="flex items-center justify-between gap-2 px-3 py-2 bg-neutral-50 dark:bg-dark-300 rounded-lg">
                      <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400 shrink-0">Before Hash</span>
                      <span className="text-xs font-mono text-neutral-900 dark:text-neutral-100">{log.before_hash.substring(0, 12)}…</span>
                    </div>
                  )}
                  {log.after_hash && (
                    <div className="flex items-center justify-between gap-2 px-3 py-2 bg-neutral-50 dark:bg-dark-300 rounded-lg">
                      <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400 shrink-0">After Hash</span>
                      <span className="text-xs font-mono text-neutral-900 dark:text-neutral-100">{log.after_hash.substring(0, 12)}…</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Affected elements */}
              {log.affected_elements?.length > 0 && (
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-300">Affected Elements</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {log.affected_elements.map((el, i) => (
                      <div key={i} className="flex items-center gap-2 px-3 py-2 bg-neutral-50 dark:bg-dark-300 rounded-lg">
                        <Target className="h-3.5 w-3.5 text-neutral-400 shrink-0" />
                        <span className="text-xs font-mono text-neutral-700 dark:text-neutral-300 truncate">{el}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Diff data */}
              {log.diff_data && (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-300">Changes Overview</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="px-4 py-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl text-center">
                      <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400">+{log.diff_data.stats?.charactersAdded || 0}</p>
                      <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-0.5">Characters Added</p>
                    </div>
                    <div className="px-4 py-3 bg-red-50 dark:bg-red-900/20 rounded-xl text-center">
                      <p className="text-xl font-bold text-red-600 dark:text-red-400">-{log.diff_data.stats?.charactersRemoved || 0}</p>
                      <p className="text-xs text-red-600 dark:text-red-400 mt-0.5">Characters Removed</p>
                    </div>
                  </div>

                  {(log.diff_data.added?.length > 0 || log.diff_data.removed?.length > 0 || log.diff_data.modified?.length > 0) && (
                    <div className="space-y-3">
                      {log.diff_data.added?.length > 0 && (
                        <div className="space-y-1.5">
                          <h4 className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                            <Plus className="h-3.5 w-3.5" /> Added ({log.diff_data.added.length})
                          </h4>
                          <div className="max-h-28 overflow-y-auto space-y-1">
                            {log.diff_data.added.map((line, i) => (
                              <div key={i} className="text-xs bg-emerald-50 dark:bg-emerald-900/20 p-2 rounded font-mono overflow-x-auto">
                                Line {line.line}: {line.content}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {log.diff_data.removed?.length > 0 && (
                        <div className="space-y-1.5">
                          <h4 className="text-xs font-semibold text-red-600 dark:text-red-400 flex items-center gap-1">
                            <Trash2 className="h-3.5 w-3.5" /> Removed ({log.diff_data.removed.length})
                          </h4>
                          <div className="max-h-28 overflow-y-auto space-y-1">
                            {log.diff_data.removed.map((line, i) => (
                              <div key={i} className="text-xs bg-red-50 dark:bg-red-900/20 p-2 rounded font-mono overflow-x-auto">
                                Line {line.line}: {line.content}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {log.diff_data.modified?.length > 0 && (
                        <div className="space-y-1.5">
                          <h4 className="text-xs font-semibold text-primary-600 dark:text-primary-400 flex items-center gap-1">
                            <Edit className="h-3.5 w-3.5" /> Modified ({log.diff_data.modified.length})
                          </h4>
                          <div className="max-h-28 overflow-y-auto space-y-1">
                            {log.diff_data.modified.map((line, i) => (
                              <div key={i} className="text-xs bg-primary-50 dark:bg-primary-900/20 p-2 rounded font-mono overflow-x-auto">
                                <div className="text-red-600 dark:text-red-400">- Line {line.line}: {line.before}</div>
                                <div className="text-emerald-600 dark:text-emerald-400">+ Line {line.line}: {line.after}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Additional data (JSON) */}
              {log.changes && (
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-300">Additional Data</h3>
                  <div className="bg-neutral-50 dark:bg-dark-300 rounded-lg p-3 overflow-x-auto max-h-48 overflow-y-auto">
                    <pre className="text-xs text-neutral-700 dark:text-neutral-300 font-mono">
                      {JSON.stringify(log.changes, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </CardContent>

            <div className="flex justify-end px-4 sm:px-6 py-4 border-t border-neutral-100 dark:border-neutral-700/40">
              <Button variant="outline" size="sm" onClick={onClose}>
                Close
              </Button>
            </div>
          </Card>
        </motion.div>
      </div>
    </motion.div>
  )
}

export default EditLogsDashboard
